/**
 * TagsAdminPage - Administracao de Tags BLE/GPS
 * ACESSO RESTRITO: Apenas usuarios com isSuperAdmin = true
 * Suporta: Vernal, K-Tag, Traqcare
 */

import {
 useState, useMemo, useCallback, useEffect 
} from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Stack,
  Button,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  InputAdornment,
  Chip,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Tab,
  Tabs,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import SyncIcon from '@mui/icons-material/Sync';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import PendingIcon from '@mui/icons-material/Pending';
import SettingsIcon from '@mui/icons-material/Settings';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import LockIcon from '@mui/icons-material/Lock';
import DevicesIcon from '@mui/icons-material/Devices';
import LinkIcon from '@mui/icons-material/Link';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import * as XLSX from 'xlsx';

import { useTranslation } from '../common/components/LocalizationProvider';
import PageLayout from '../common/components/PageLayout';
import SettingsMenu from './components/SettingsMenu';
import { useSuperAdmin } from '../common/util/permissions';
import fetchOrThrow from '../common/util/fetchOrThrow';
import { useEffectAsync } from '../reactHelper';
import { formatDate } from '../common/util/formatter';
import { usePremiumTokens } from '../common/theme/usePremiumTokens';
import { PremiumStatCards, PremiumEmptyState } from '../common/components/premium';

// Access Denied Component
const AccessDeniedPage = ({ tokens, t }) => (
  <PageLayout menu={<SettingsMenu />} breadcrumbs={['settingsTitle', 'tagsAdmin']}>
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '60vh',
        gap: 3,
      }}
    >
      <LockIcon sx={{ fontSize: 80, color: tokens?.semantic?.error || '#ef4444' }} />
      <Typography variant="h4" sx={{ color: tokens?.text?.primary, fontWeight: 600 }}>
        {t('accessDenied') || 'Acesso Negado'}
      </Typography>
      <Typography variant="body1" sx={{ color: tokens?.text?.secondary, textAlign: 'center', maxWidth: 400 }}>
        {t('tagsAdminSuperAdminOnly') || 'Esta pagina e restrita a super administradores. Contate o administrador do sistema para obter acesso.'}
      </Typography>
      <Alert severity="warning" sx={{ maxWidth: 500 }}>
        {t('tagsAdminRequiresAttribute') || 'Para acessar esta pagina, o usuario precisa ter o atributo isSuperAdmin = true configurado no banco de dados.'}
      </Alert>
    </Box>
  </PageLayout>
);

// Provider Config Dialog
const ProviderConfigDialog = ({ open, onClose, provider, onSave, tokens, t }) => {
  const [config, setConfig] = useState({});

  // Reset config when provider changes or dialog opens
  useEffect(() => {
    if (open && provider?.attributes) {
      // Handle both object and string formats
      const attrs = typeof provider.attributes === 'string'
        ? JSON.parse(provider.attributes)
        : provider.attributes;
      setConfig(attrs);
    } else if (!open) {
      setConfig({});
    }
  }, [provider, open]);

  const handleSave = () => {
    onSave(provider.id, config);
    onClose();
  };

  if (!provider) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ bgcolor: tokens?.surface?.elevated, color: tokens?.text?.primary }}>
        {t('configureProvider') || 'Configurar Provedor'}: {provider.name?.toUpperCase()}
      </DialogTitle>
      <DialogContent sx={{ bgcolor: tokens?.surface?.elevated, pt: 2 }}>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {provider.name === 'vernal' && (
            <>
              <TextField
                label="CID"
                value={config.cid || ''}
                onChange={(e) => setConfig({ ...config, cid: e.target.value })}
                fullWidth
                size="small"
              />
              <TextField
                label={t('sharedUsername') || 'Usuario'}
                value={config.username || ''}
                onChange={(e) => setConfig({ ...config, username: e.target.value })}
                fullWidth
                size="small"
              />
              <TextField
                label={t('sharedPassword') || 'Senha'}
                type="password"
                value={config.password || ''}
                onChange={(e) => setConfig({ ...config, password: e.target.value })}
                fullWidth
                size="small"
              />
            </>
          )}
          {provider.name === 'ktag' && (
            <>
              <TextField
                label={t('sharedUsername') || 'Usuario'}
                value={config.username || ''}
                onChange={(e) => setConfig({ ...config, username: e.target.value })}
                fullWidth
                size="small"
              />
              <TextField
                label={t('sharedPassword') || 'Senha'}
                type="password"
                value={config.password || ''}
                onChange={(e) => setConfig({ ...config, password: e.target.value })}
                fullWidth
                size="small"
              />
            </>
          )}
          {provider.name === 'traqcare' && (
            <TextField
              label="API Token"
              value={config.apiToken || ''}
              onChange={(e) => setConfig({ ...config, apiToken: e.target.value })}
              fullWidth
              size="small"
            />
          )}
          {provider.name === 'qld' && (
            <>
              <TextField
                label={t('qldLocalName') || 'Local Name (Identificador)'}
                value={config.localName || ''}
                onChange={(e) => setConfig({ ...config, localName: e.target.value })}
                fullWidth
                size="small"
                helperText={t('qldLocalNameHint') || 'Nome identificador da plataforma no QLD'}
              />
              <Alert severity="info" sx={{ mt: 1 }}>
                {t('qldCertificateInfo') || 'QLD usa autenticacao mTLS com certificados. Configure os certificados abaixo.'}
              </Alert>
              <Typography variant="subtitle2" sx={{ mt: 2, mb: 1, fontWeight: 600 }}>
                {t('qldCertificatePem') || 'Certificado (cert.pem)'}
              </Typography>
              <TextField
                multiline
                rows={4}
                value={config.certPem || ''}
                onChange={(e) => setConfig({ ...config, certPem: e.target.value })}
                fullWidth
                size="small"
                placeholder="-----BEGIN CERTIFICATE-----&#10;...&#10;-----END CERTIFICATE-----"
                InputProps={{
                  sx: { fontFamily: 'monospace', fontSize: '0.75rem' },
                }}
              />
              <Box sx={{ mt: 1, mb: 2 }}>
                <Button
                  variant="outlined"
                  size="small"
                  component="label"
                  startIcon={<UploadFileIcon />}
                >
                  {t('uploadCertificate') || 'Carregar cert.pem'}
                  <input
                    type="file"
                    hidden
                    accept=".pem,.crt,.cer"
                    onChange={(e) => {
                      const file = e.target.files[0];
                      if (file) {
                        const reader = new FileReader();
                        reader.onload = (ev) => setConfig({ ...config, certPem: ev.target.result });
                        reader.readAsText(file);
                      }
                    }}
                  />
                </Button>
              </Box>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                {t('qldPrivateKeyPem') || 'Chave Privada (key.pem)'}
              </Typography>
              <TextField
                multiline
                rows={4}
                value={config.keyPem || ''}
                onChange={(e) => setConfig({ ...config, keyPem: e.target.value })}
                fullWidth
                size="small"
                placeholder="-----BEGIN PRIVATE KEY-----&#10;...&#10;-----END PRIVATE KEY-----"
                InputProps={{
                  sx: { fontFamily: 'monospace', fontSize: '0.75rem' },
                }}
              />
              <Box sx={{ mt: 1 }}>
                <Button
                  variant="outlined"
                  size="small"
                  component="label"
                  startIcon={<UploadFileIcon />}
                >
                  {t('uploadPrivateKey') || 'Carregar key.pem'}
                  <input
                    type="file"
                    hidden
                    accept=".pem,.key"
                    onChange={(e) => {
                      const file = e.target.files[0];
                      if (file) {
                        const reader = new FileReader();
                        reader.onload = (ev) => setConfig({ ...config, keyPem: ev.target.result });
                        reader.readAsText(file);
                      }
                    }}
                  />
                </Button>
              </Box>
              {(config.certPem || config.keyPem) && (
                <Alert severity="success" sx={{ mt: 2 }}>
                  {t('qldCertificatesConfigured') || 'Certificados configurados. Clique em Salvar para aplicar.'}
                </Alert>
              )}
            </>
          )}
          <TextField
            label="API URL"
            value={provider.apiUrl || ''}
            disabled
            fullWidth
            size="small"
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ bgcolor: tokens?.surface?.elevated }}>
        <Button onClick={onClose}>{t('sharedCancel') || 'Cancelar'}</Button>
        <Button onClick={handleSave} variant="contained" color="primary">
          {t('sharedSave') || 'Salvar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Add Single Tag Dialog
const AddTagDialog = ({ open, onClose, providers, onAdd, tokens, t }) => {
  const [selectedProvider, setSelectedProvider] = useState('');
  const [externalId, setExternalId] = useState('');
  const [name, setName] = useState('');
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState('');

  const handleAdd = async () => {
    if (!selectedProvider || !externalId.trim()) {
      setError(t('fillRequiredFields') || 'Preencha os campos obrigatórios');
      return;
    }

    setAdding(true);
    setError('');

    try {
      const response = await fetch(`/api/tags/import/${selectedProvider}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify([{
          externalId: externalId.trim(),
          name: name.trim() || externalId.trim(),
        }]),
      });

      if (!response.ok) throw new Error('Failed to add tag');

      const result = await response.json();
      if (result.imported > 0) {
        onAdd();
        handleClose();
      } else if (result.skipped > 0) {
        setError(t('tagAlreadyExists') || 'Tag já existe');
      } else {
        setError(result.errors?.join(', ') || 'Erro ao adicionar');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setAdding(false);
    }
  };

  const handleClose = () => {
    setSelectedProvider('');
    setExternalId('');
    setName('');
    setError('');
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ bgcolor: tokens?.surface?.elevated, color: tokens?.text?.primary }}>
        <LocalOfferIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
        {t('addTag') || 'Adicionar Tag'}
      </DialogTitle>
      <DialogContent sx={{ bgcolor: tokens?.surface?.elevated, pt: 2 }}>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            select
            label={t('selectProvider') || 'Provedor'}
            value={selectedProvider}
            onChange={(e) => setSelectedProvider(e.target.value)}
            fullWidth
            size="small"
            required
            SelectProps={{ native: true }}
          >
            <option value="">{t('sharedSelect') || 'Selecione...'}</option>
            {providers.map((p) => (
              <option key={p.id} value={p.id}>{p.name?.toUpperCase()}</option>
            ))}
          </TextField>
          <TextField
            label={t('externalId') || 'ID Externo (MAC/SN)'}
            value={externalId}
            onChange={(e) => setExternalId(e.target.value)}
            fullWidth
            size="small"
            required
            placeholder="F6:C1:DD:B1:65:08"
            helperText={t('externalIdHint') || 'MAC address ou número de série da tag'}
          />
          <TextField
            label={t('sharedName') || 'Nome (opcional)'}
            value={name}
            onChange={(e) => setName(e.target.value)}
            fullWidth
            size="small"
            placeholder="Tag Escritório"
          />
          {error && (
            <Alert severity="error">{error}</Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ bgcolor: tokens?.surface?.elevated }}>
        <Button onClick={handleClose}>{t('sharedCancel') || 'Cancelar'}</Button>
        <Button
          onClick={handleAdd}
          variant="contained"
          color="primary"
          disabled={!selectedProvider || !externalId.trim() || adding}
        >
          {adding ? (t('sharedAdding') || 'Adicionando...') : (t('sharedAdd') || 'Adicionar')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Link Tag to Device Dialog
const LinkDeviceDialog = ({ open, onClose, tag, devices, onLink, tokens, t }) => {
  const [selectedDevice, setSelectedDevice] = useState('');
  const [searchDevice, setSearchDevice] = useState('');

  const filteredDevices = useMemo(() => {
    if (!searchDevice.trim()) return devices.slice(0, 50);
    const lower = searchDevice.toLowerCase();
    return devices.filter(
      (d) => d.name?.toLowerCase().includes(lower) || d.uniqueId?.toLowerCase().includes(lower)
    ).slice(0, 50);
  }, [devices, searchDevice]);

  const handleLink = () => {
    if (selectedDevice) {
      onLink(tag.id, parseInt(selectedDevice, 10));
      onClose();
    }
  };

  if (!tag) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ bgcolor: tokens?.surface?.elevated, color: tokens?.text?.primary }}>
        {t('linkTagToDevice') || 'Vincular Tag ao Dispositivo'}
      </DialogTitle>
      <DialogContent sx={{ bgcolor: tokens?.surface?.elevated, pt: 2 }}>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Tag: <strong>{tag.name || tag.externalId}</strong>
          </Typography>
          <TextField
            label={t('searchDevice') || 'Buscar Dispositivo'}
            value={searchDevice}
            onChange={(e) => setSearchDevice(e.target.value)}
            fullWidth
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
          <TextField
            select
            label={t('selectDevice') || 'Selecionar Dispositivo'}
            value={selectedDevice}
            onChange={(e) => setSelectedDevice(e.target.value)}
            fullWidth
            size="small"
            SelectProps={{ native: true }}
          >
            <option value="">{t('sharedSelect') || 'Selecione...'}</option>
            {filteredDevices.map((d) => (
              <option key={d.id} value={d.id}>
                {d.name} ({d.uniqueId})
              </option>
            ))}
          </TextField>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ bgcolor: tokens?.surface?.elevated }}>
        <Button onClick={onClose}>{t('sharedCancel') || 'Cancelar'}</Button>
        <Button onClick={handleLink} variant="contained" color="primary" disabled={!selectedDevice}>
          {t('sharedLink') || 'Vincular'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Import Tags Dialog with Excel Support
const ImportTagsDialog = ({ open, onClose, providers, onImport, tokens, t }) => {
  const [selectedProvider, setSelectedProvider] = useState('');
  const [fileData, setFileData] = useState([]);
  const [fileName, setFileName] = useState('');
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);

  // Column mapping for all providers (generic + K-Tag specific)
  const columnMapping = {
    // Generic (works for all providers)
    'externalId': 'externalId',
    'external_id': 'externalId',
    'id': 'externalId',
    'ID': 'externalId',
    'mac': 'externalId',
    'MAC': 'externalId',
    'macAddress': 'externalId',
    'mac_address': 'externalId',
    'sn': 'externalId',
    'SN': 'externalId',
    'serial': 'externalId',
    'name': 'name',
    'Name': 'name',
    'nome': 'name',
    'Nome': 'name',
    'description': 'name',
    // K-Tag specific (Chinese)
    'SN码': 'externalId',
    'Key名称': 'name',
    'MAC地址': 'mac',
    'privateKey值': 'privateKey',
    'hashedAdvKey值': 'hashedKey',
    // K-Tag specific (English)
    'Private Key': 'privateKey',
    'Hashed Key': 'hashedKey',
    'privateKey': 'privateKey',
    'hashedKey': 'hashedKey',
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setFileName(file.name);
    setResult(null);

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet);

        // Map columns to our expected format
        const mappedData = jsonData.map((row) => {
          const mapped = {};
          Object.keys(row).forEach((key) => {
            const mappedKey = columnMapping[key] || key;
            mapped[mappedKey] = row[key];
          });
          return mapped;
        });

        setFileData(mappedData);
      } catch (error) {
        console.error('Error parsing Excel file:', error);
        setResult({ error: 'Erro ao ler arquivo Excel' });
      }
    };
    reader.readAsArrayBuffer(file);
  };

  const handleImport = async () => {
    if (!selectedProvider || fileData.length === 0) return;

    setImporting(true);
    setResult(null);

    try {
      const response = await fetch(`/api/tags/import/${selectedProvider}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(fileData),
      });

      if (!response.ok) {
        throw new Error('Import failed');
      }

      const resultData = await response.json();
      setResult(resultData);
      onImport();
    } catch (error) {
      console.error('Import error:', error);
      setResult({ error: error.message });
    } finally {
      setImporting(false);
    }
  };

  const handleClose = () => {
    setFileData([]);
    setFileName('');
    setSelectedProvider('');
    setResult(null);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ bgcolor: tokens?.surface?.elevated, color: tokens?.text?.primary }}>
        <UploadFileIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
        {t('importTags') || 'Importar Tags (Excel)'}
      </DialogTitle>
      <DialogContent sx={{ bgcolor: tokens?.surface?.elevated, pt: 2 }}>
        <Stack spacing={3} sx={{ mt: 1 }}>
          {/* Provider Selection */}
          <TextField
            select
            label={t('selectProvider') || 'Selecionar Provedor'}
            value={selectedProvider}
            onChange={(e) => setSelectedProvider(e.target.value)}
            fullWidth
            size="small"
            SelectProps={{ native: true }}
          >
            <option value="">{t('sharedSelect') || 'Selecione...'}</option>
            {providers.map((p) => (
              <option key={p.id} value={p.id}>{p.name?.toUpperCase()}</option>
            ))}
          </TextField>

          {/* File Upload */}
          <Box
            sx={{
              border: `2px dashed ${tokens?.border?.default || '#444'}`,
              borderRadius: 2,
              p: 3,
              textAlign: 'center',
              bgcolor: tokens?.surface?.glass,
              cursor: 'pointer',
              '&:hover': { borderColor: tokens?.accent?.primary },
            }}
            component="label"
          >
            <input
              type="file"
              accept=".xlsx,.xls,.csv"
              hidden
              onChange={handleFileUpload}
            />
            <CloudUploadIcon sx={{ fontSize: 48, color: tokens?.text?.secondary, mb: 1 }} />
            <Typography variant="body1" color="text.secondary">
              {fileName || (t('dropExcelFile') || 'Clique ou arraste um arquivo Excel (.xlsx)')}
            </Typography>
            {fileData.length > 0 && (
              <Chip
                label={`${fileData.length} tags encontradas`}
                color="success"
                sx={{ mt: 1 }}
              />
            )}
          </Box>

          {/* Preview */}
          {fileData.length > 0 && (
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                {t('preview') || 'Preview'} ({Math.min(5, fileData.length)} de {fileData.length}):
              </Typography>
              <TableContainer component={Paper} sx={{ maxHeight: 200 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>ID Externo</TableCell>
                      <TableCell>Nome</TableCell>
                      <TableCell>MAC</TableCell>
                      <TableCell>Private Key</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {fileData.slice(0, 5).map((row, idx) => (
                      <TableRow key={idx}>
                        <TableCell>{row.externalId}</TableCell>
                        <TableCell>{row.name}</TableCell>
                        <TableCell>{row.mac}</TableCell>
                        <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.7rem' }}>
                          {row.privateKey?.substring(0, 20)}...
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}

          {/* Result */}
          {result && (
            <Alert severity={result.error ? 'error' : 'success'}>
              {result.error ? (
                result.error
              ) : (
                <>
                  Importadas: <strong>{result.imported}</strong> |
                  Ignoradas (duplicadas): <strong>{result.skipped}</strong>
                  {result.errors?.length > 0 && (
                    <Box sx={{ mt: 1 }}>Erros: {result.errors.join(', ')}</Box>
                  )}
                </>
              )}
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ bgcolor: tokens?.surface?.elevated }}>
        <Button onClick={handleClose}>{t('sharedCancel') || 'Cancelar'}</Button>
        <Button
          onClick={handleImport}
          variant="contained"
          color="primary"
          disabled={!selectedProvider || fileData.length === 0 || importing}
          startIcon={<UploadFileIcon />}
        >
          {importing ? (t('importing') || 'Importando...') : (t('import') || 'Importar')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const TagsAdminPage = () => {
  const navigate = useNavigate();
  const t = useTranslation();
  const tokens = usePremiumTokens();
  const isSuperAdmin = useSuperAdmin();

  const [activeTab, setActiveTab] = useState(0);
  const [timestamp, setTimestamp] = useState(Date.now());
  const [providers, setProviders] = useState([]);
  const [tags, setTags] = useState([]);
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [statusFilter, setStatusFilter] = useState('');
  const [providerFilter, setProviderFilter] = useState('');
  const [searchText, setSearchText] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  // Dialog states
  const [configDialogOpen, setConfigDialogOpen] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [selectedTag, setSelectedTag] = useState(null);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [addTagDialogOpen, setAddTagDialogOpen] = useState(false);

  // Fetch data
  useEffectAsync(async () => {
    if (!isSuperAdmin) return;
    setLoading(true);
    try {
      const [providersRes, tagsRes, devicesRes] = await Promise.all([
        fetchOrThrow('/api/tags/providers'),
        fetchOrThrow('/api/tags'),
        fetchOrThrow('/api/devices'),
      ]);
      setProviders(await providersRes.json());
      setTags(await tagsRes.json());
      setDevices(await devicesRes.json());
    } catch (error) {
      console.error('Error fetching tags data:', error);
    } finally {
      setLoading(false);
    }
  }, [timestamp, isSuperAdmin]);

  // Stats
  const stats = useMemo(() => {
    const pending = tags.filter((t) => t.status === 'pending').length;
    const approved = tags.filter((t) => t.status === 'approved').length;
    const rejected = tags.filter((t) => t.status === 'rejected').length;
    const linked = tags.filter((t) => t.deviceId).length;
    return { total: tags.length, pending, approved, rejected, linked };
  }, [tags]);

  const statFilters = useMemo(() => [
    { value: '', label: t('sharedAll') || 'Todas', count: stats.total, color: tokens?.accent?.primary, Icon: LocalOfferIcon },
    { value: 'pending', label: t('sharedPending') || 'Pendentes', count: stats.pending, color: tokens?.semantic?.warning, Icon: PendingIcon },
    { value: 'approved', label: t('sharedApproved') || 'Aprovadas', count: stats.approved, color: tokens?.semantic?.success, Icon: CheckCircleIcon },
    { value: 'rejected', label: t('sharedRejected') || 'Rejeitadas', count: stats.rejected, color: tokens?.semantic?.error, Icon: CancelIcon },
  ], [t, stats, tokens]);

  // Filtering
  const filteredTags = useMemo(() => {
    let result = tags;
    if (statusFilter) {
      result = result.filter((t) => t.status === statusFilter);
    }
    if (providerFilter) {
      result = result.filter((t) => t.providerId === parseInt(providerFilter, 10));
    }
    if (searchText.trim()) {
      const lower = searchText.toLowerCase();
      result = result.filter(
        (t) =>
          t.name?.toLowerCase().includes(lower) ||
          t.externalId?.toLowerCase().includes(lower) ||
          t.id?.toString().includes(lower)
      );
    }
    return result;
  }, [tags, statusFilter, providerFilter, searchText]);

  const paginatedTags = useMemo(() => {
    const start = page * rowsPerPage;
    return filteredTags.slice(start, start + rowsPerPage);
  }, [filteredTags, page, rowsPerPage]);

  // Handlers
  const handleSync = useCallback(async () => {
    setSyncing(true);
    try {
      await fetchOrThrow('/api/tags/sync', { method: 'POST' });
      setTimestamp(Date.now());
    } catch (error) {
      console.error('Sync error:', error);
    } finally {
      setSyncing(false);
    }
  }, []);

  const handleApprove = useCallback(async (tagId) => {
    try {
      await fetchOrThrow(`/api/tags/${tagId}/approve`, { method: 'POST' });
      setTimestamp(Date.now());
    } catch (error) {
      console.error('Approve error:', error);
    }
  }, []);

  const handleReject = useCallback(async (tagId) => {
    try {
      await fetchOrThrow(`/api/tags/${tagId}/reject`, { method: 'POST' });
      setTimestamp(Date.now());
    } catch (error) {
      console.error('Reject error:', error);
    }
  }, []);

  const handleLinkDevice = useCallback(async (tagId, deviceId) => {
    try {
      await fetchOrThrow(`/api/tags/${tagId}/link`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ deviceId }),
      });
      setTimestamp(Date.now());
    } catch (error) {
      console.error('Link error:', error);
    }
  }, []);

  const handleSaveProviderConfig = useCallback(async (providerId, config) => {
    try {
      // Find the provider to get all current fields
      const provider = providers.find((p) => p.id === providerId);
      if (!provider) return;

      // Send complete provider object with updated attributes
      await fetchOrThrow(`/api/tags/providers/${providerId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...provider,
          attributes: config, // Backend handles serialization
        }),
      });
      setTimestamp(Date.now());
    } catch (error) {
      console.error('Save provider config error:', error);
    }
  }, [providers]);

  const handleToggleProvider = useCallback(async (provider) => {
    try {
      await fetchOrThrow(`/api/tags/providers/${provider.id}/toggle`, {
        method: 'POST',
      });
      setTimestamp(Date.now());
    } catch (error) {
      console.error('Toggle provider error:', error);
    }
  }, []);

  const getProviderName = useCallback((providerId) => {
    const provider = providers.find((p) => p.id === providerId);
    return provider?.name?.toUpperCase() || `ID: ${providerId}`;
  }, [providers]);

  const getDeviceName = useCallback((deviceId) => {
    const device = devices.find((d) => d.id === deviceId);
    return device?.name || `ID: ${deviceId}`;
  }, [devices]);

  // Header styles
  const headerCellSx = {
    fontWeight: 600,
    fontSize: '0.75rem',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    color: tokens?.text?.secondary,
    borderBottom: `1px solid ${tokens?.border?.subtle || 'rgba(255,255,255,0.06)'}`,
    py: 1.5,
  };

  // Access check
  if (!isSuperAdmin) {
    return <AccessDeniedPage tokens={tokens} t={t} />;
  }

  return (
    <PageLayout menu={<SettingsMenu />} breadcrumbs={['settingsTitle', 'tagsAdmin']}>
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
        <Box sx={{ flex: 1, p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>

          {/* Header */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h5" sx={{ color: tokens?.text?.primary, fontWeight: 600 }}>
              <LocalOfferIcon sx={{ mr: 1, verticalAlign: 'middle', color: tokens?.accent?.primary }} />
              {t('tagsAdmin') || 'Administracao de Tags'}
            </Typography>
            <Stack direction="row" spacing={1}>
              <Button
                variant="outlined"
                size="small"
                startIcon={<RefreshIcon />}
                onClick={() => setTimestamp(Date.now())}
                disabled={loading}
              >
                {t('sharedRefresh') || 'Atualizar'}
              </Button>
              <Button
                variant="contained"
                size="small"
                startIcon={<LocalOfferIcon />}
                onClick={() => setAddTagDialogOpen(true)}
                sx={{
                  backgroundColor: tokens?.semantic?.success,
                  '&:hover': { backgroundColor: tokens?.semantic?.success + 'cc' },
                }}
              >
                {t('addTag') || 'Adicionar Tag'}
              </Button>
              <Button
                variant="outlined"
                size="small"
                startIcon={<UploadFileIcon />}
                onClick={() => setImportDialogOpen(true)}
                sx={{
                  borderColor: tokens?.accent?.primary,
                  color: tokens?.accent?.primary,
                  '&:hover': { borderColor: tokens?.accent?.hover, bgcolor: `${tokens?.accent?.primary}10` },
                }}
              >
                {t('importCsv') || 'Importar CSV'}
              </Button>
              <Button
                variant="outlined"
                size="small"
                startIcon={<SyncIcon />}
                onClick={handleSync}
                disabled={syncing}
                color="warning"
                title={t('syncWarning') || 'Sincroniza TODAS as tags do provedor'}
              >
                {syncing ? (t('sharedSyncing') || 'Sincronizando...') : (t('syncTags') || 'Sync API')}
              </Button>
            </Stack>
          </Box>

          {/* Tabs */}
          <Tabs
            value={activeTab}
            onChange={(_, v) => setActiveTab(v)}
            sx={{
              borderBottom: `1px solid ${tokens?.border?.subtle}`,
              '& .MuiTab-root': { color: tokens?.text?.secondary },
              '& .Mui-selected': { color: tokens?.accent?.primary },
            }}
          >
            <Tab label={t('sharedTags') || 'Tags'} icon={<LocalOfferIcon />} iconPosition="start" />
            <Tab label={t('sharedProviders') || 'Provedores'} icon={<SettingsIcon />} iconPosition="start" />
          </Tabs>

          {/* Tab 0: Tags */}
          {activeTab === 0 && (
            <>
              <PremiumStatCards
                filters={statFilters}
                activeFilter={statusFilter}
                onFilterChange={(v) => { setStatusFilter(v); setPage(0); }}
              />

              {/* Filters */}
              <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                <TextField
                  size="small"
                  value={searchText}
                  onChange={(e) => { setSearchText(e.target.value); setPage(0); }}
                  placeholder={t('sharedSearch') || 'Buscar...'}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ color: tokens?.accent?.primary }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ minWidth: 250 }}
                />
                <TextField
                  select
                  size="small"
                  value={providerFilter}
                  onChange={(e) => { setProviderFilter(e.target.value); setPage(0); }}
                  SelectProps={{ native: true }}
                  sx={{ minWidth: 150 }}
                >
                  <option value="">{t('allProviders') || 'Todos Provedores'}</option>
                  {providers.map((p) => (
                    <option key={p.id} value={p.id}>{p.name?.toUpperCase()}</option>
                  ))}
                </TextField>
              </Box>

              {/* Tags count */}
              <Typography variant="body2" color="text.secondary">
                {filteredTags.length} tags {(statusFilter || providerFilter || searchText) && '(filtrado)'}
              </Typography>

              {/* Tags Table */}
              <Paper
                sx={{
                  flex: 1,
                  overflow: 'hidden',
                  borderRadius: tokens?.radius?.lg || '12px',
                  backgroundColor: tokens?.surface?.glass,
                  border: `1px solid ${tokens?.border?.subtle || 'rgba(255,255,255,0.06)'}`,
                }}
              >
                <TableContainer sx={{ maxHeight: 500 }}>
                  <Table stickyHeader size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={headerCellSx}>ID</TableCell>
                        <TableCell sx={headerCellSx}>{t('sharedProvider') || 'Provedor'}</TableCell>
                        <TableCell sx={headerCellSx}>{t('externalId') || 'ID Externo'}</TableCell>
                        <TableCell sx={headerCellSx}>{t('sharedName') || 'Nome'}</TableCell>
                        <TableCell sx={headerCellSx}>{t('sharedStatus') || 'Status'}</TableCell>
                        <TableCell sx={headerCellSx}>{t('linkedDevice') || 'Dispositivo'}</TableCell>
                        <TableCell sx={headerCellSx}>{t('lastSync') || 'Ultima Sync'}</TableCell>
                        <TableCell sx={{ ...headerCellSx, textAlign: 'right' }}>{t('sharedActions') || 'Acoes'}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {paginatedTags.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={8} sx={{ border: 0, p: 0 }}>
                            <PremiumEmptyState
                              icon={LocalOfferIcon}
                              title={t('noTags') || 'Nenhuma tag encontrada'}
                              subtitle={t('noTagsHint') || 'Clique em "Sincronizar Tags" para importar tags dos provedores'}
                              hasFilters={!!(statusFilter || providerFilter || searchText)}
                              onClearFilters={() => { setStatusFilter(''); setProviderFilter(''); setSearchText(''); }}
                            />
                          </TableCell>
                        </TableRow>
                      ) : (
                        paginatedTags.map((tag) => (
                          <TableRow key={tag.id} hover>
                            <TableCell>{tag.id}</TableCell>
                            <TableCell>
                              <Chip
                                label={getProviderName(tag.providerId)}
                                size="small"
                                sx={{
                                  bgcolor: tokens?.surface?.hover,
                                  color: tokens?.text?.primary,
                                }}
                              />
                            </TableCell>
                            <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                              {tag.externalId}
                            </TableCell>
                            <TableCell>{tag.name || '-'}</TableCell>
                            <TableCell>
                              <Chip
                                label={tag.status}
                                size="small"
                                color={
                                  tag.status === 'approved' ? 'success' :
                                  tag.status === 'rejected' ? 'error' : 'warning'
                                }
                                icon={
                                  tag.status === 'approved' ? <CheckCircleIcon /> :
                                  tag.status === 'rejected' ? <CancelIcon /> : <PendingIcon />
                                }
                              />
                            </TableCell>
                            <TableCell>
                              {tag.deviceId ? (
                                <Chip
                                  label={getDeviceName(tag.deviceId)}
                                  size="small"
                                  icon={<DevicesIcon />}
                                  sx={{ bgcolor: tokens?.semantic?.info + '20', color: tokens?.semantic?.info }}
                                />
                              ) : '-'}
                            </TableCell>
                            <TableCell sx={{ fontSize: '0.8rem' }}>
                              {tag.lastSync ? formatDate(tag.lastSync) : '-'}
                            </TableCell>
                            <TableCell align="right">
                              <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                                {tag.status === 'pending' && (
                                  <>
                                    <Tooltip title={t('sharedApprove') || 'Aprovar'}>
                                      <IconButton
                                        size="small"
                                        onClick={() => handleApprove(tag.id)}
                                        sx={{ color: tokens?.semantic?.success }}
                                      >
                                        <CheckCircleIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                    <Tooltip title={t('sharedReject') || 'Rejeitar'}>
                                      <IconButton
                                        size="small"
                                        onClick={() => handleReject(tag.id)}
                                        sx={{ color: tokens?.semantic?.error }}
                                      >
                                        <CancelIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                  </>
                                )}
                                {tag.status === 'approved' && !tag.deviceId && (
                                  <Tooltip title={t('linkToDevice') || 'Vincular a Dispositivo'}>
                                    <IconButton
                                      size="small"
                                      onClick={() => { setSelectedTag(tag); setLinkDialogOpen(true); }}
                                      sx={{ color: tokens?.accent?.primary }}
                                    >
                                      <LinkIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                )}
                              </Stack>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
                {filteredTags.length > 0 && (
                  <TablePagination
                    component="div"
                    count={filteredTags.length}
                    page={page}
                    onPageChange={(_, newPage) => setPage(newPage)}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                    rowsPerPageOptions={[10, 25, 50, 100]}
                    labelRowsPerPage={t('rowsPerPage') || 'Por pagina'}
                  />
                )}
              </Paper>
            </>
          )}

          {/* Tab 1: Providers */}
          {activeTab === 1 && (
            <Paper
              sx={{
                borderRadius: tokens?.radius?.lg || '12px',
                backgroundColor: tokens?.surface?.glass,
                border: `1px solid ${tokens?.border?.subtle || 'rgba(255,255,255,0.06)'}`,
                overflow: 'hidden',
              }}
            >
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={headerCellSx}>ID</TableCell>
                      <TableCell sx={headerCellSx}>{t('sharedName') || 'Nome'}</TableCell>
                      <TableCell sx={headerCellSx}>API URL</TableCell>
                      <TableCell sx={headerCellSx}>{t('authType') || 'Autenticacao'}</TableCell>
                      <TableCell sx={headerCellSx}>{t('sharedStatus') || 'Status'}</TableCell>
                      <TableCell sx={{ ...headerCellSx, textAlign: 'right' }}>{t('sharedActions') || 'Acoes'}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {providers.map((provider) => (
                      <TableRow key={provider.id} hover>
                        <TableCell>{provider.id}</TableCell>
                        <TableCell>
                          <Typography sx={{ fontWeight: 600, textTransform: 'uppercase' }}>
                            {provider.name}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                          {provider.apiUrl}
                        </TableCell>
                        <TableCell>
                          <Chip label={provider.authType} size="small" />
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={provider.active ? (t('sharedActive') || 'Ativo') : (t('sharedInactive') || 'Inativo')}
                            size="small"
                            color={provider.active ? 'success' : 'default'}
                          />
                        </TableCell>
                        <TableCell align="right">
                          <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                            <Tooltip title={t('configure') || 'Configurar'}>
                              <IconButton
                                size="small"
                                onClick={() => { setSelectedProvider(provider); setConfigDialogOpen(true); }}
                              >
                                <SettingsIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Button
                              size="small"
                              variant={provider.active ? 'outlined' : 'contained'}
                              color={provider.active ? 'error' : 'success'}
                              onClick={() => handleToggleProvider(provider)}
                            >
                              {provider.active ? (t('sharedDisable') || 'Desativar') : (t('sharedEnable') || 'Ativar')}
                            </Button>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          )}
        </Box>
      </Box>

      {/* Dialogs */}
      <ProviderConfigDialog
        open={configDialogOpen}
        onClose={() => setConfigDialogOpen(false)}
        provider={selectedProvider}
        onSave={handleSaveProviderConfig}
        tokens={tokens}
        t={t}
      />
      <LinkDeviceDialog
        open={linkDialogOpen}
        onClose={() => setLinkDialogOpen(false)}
        tag={selectedTag}
        devices={devices}
        onLink={handleLinkDevice}
        tokens={tokens}
        t={t}
      />
      <ImportTagsDialog
        open={importDialogOpen}
        onClose={() => setImportDialogOpen(false)}
        providers={providers}
        onImport={() => setTimestamp(Date.now())}
        tokens={tokens}
        t={t}
      />
      <AddTagDialog
        open={addTagDialogOpen}
        onClose={() => setAddTagDialogOpen(false)}
        providers={providers}
        onAdd={() => setTimestamp(Date.now())}
        tokens={tokens}
        t={t}
      />
    </PageLayout>
  );
};

export default TagsAdminPage;
